import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  FormControl,
  NativeSelect,
  OutlinedInput,
  TextField,
  Chip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { EFormAction, IAddAccountRequest } from "../../../api/mosaic/interface";
import { useAppSelector } from "../../../store/reduxHooks";
import { IRoles } from "../Roles/interface";
import { handleGetRolesByCompanyIdApi, handleCreateRolesApi, handleGetPermissionsApi } from "../../../api/mosaic/roles";
import { handleDepartmentsByCompanyApi } from "../../../api/mosaic/department";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";
import AddIcon from "@mui/icons-material/Add";
import { useToast } from "../../shared/Toast";
import theme from "../../../utils/theme";
import { WEBSITE_REDIRECT_OPTIONS } from "./websiteRedirectOptions";

const steps = ["Details", "Role & Department"];

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

interface IProps {
  openModal: boolean;
  handleClose: () => void;
  formAction: EFormAction;
  isLoading: boolean;
  handleUpdateUser: () => void;
  handleCreateUser: () => void;
  formData: IAddAccountRequest;
  handleOnChange: (key: string, value: string) => void;
}

const selectSx = { borderRadius: "8px" };

const UserModal: React.FC<IProps> = ({
  openModal,
  handleClose,
  formAction,
  handleCreateUser,
  handleUpdateUser,
  formData,
  handleOnChange,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const companyData = useAppSelector((state) => state.companySlice.companyData);
  const [roleData, setRoleData] = useState<IRoles[]>([]);
  const [selectedRole, setSelectedRole] = useState<IRoles>();
  const [departments, setDepartments] = useState<any[]>([]);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [globalPermissions, setGlobalPermissions] = useState<{ id: number; name: string }[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const { showSuccess, showError } = useToast();

  const handleRolesByCompany = async () => {
    try {
      const result = await handleGetRolesByCompanyIdApi(formData.companyId as number);
      setRoleData(result);
      if (formAction === EFormAction.UPDATE) {
        setSelectedRole(result.find((role) => role.id === formData.roleId));
      } else {
        const exportRole = result.find((role) => role.name === "Export");
        if (exportRole) {
          handleOnChange("roleId", String(exportRole.id));
          setSelectedRole(exportRole);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (formData.companyId) handleRolesByCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.companyId]);

  useEffect(() => {
    if (formData.companyId) {
      const fetchDepartmentsByCompany = async (companyId: number) => {
        try {
          const response = await handleDepartmentsByCompanyApi(companyId);
          setDepartments(response);
        } catch (err) {
          console.error(err);
        }
      };
      fetchDepartmentsByCompany(formData.companyId as number);
    }
  }, [formData.companyId]);

  const handleOpenCreateRole = async () => {
    setShowCreateRole(true);
    try {
      const perms = await handleGetPermissionsApi();
      setGlobalPermissions(perms);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateNewRole = async () => {
    if (!newRoleName.trim()) return;
    setIsCreatingRole(true);
    try {
      const permissionsObj = globalPermissions
        .filter((p) => selectedPermissions.includes(p.name))
        .map((p) => ({ permissionId: p.id, permissionName: p.name }));

      await handleCreateRolesApi({
        roleName: newRoleName,
        companyId: formData.companyId as number,
        permissionsObj,
      });

      // Refresh roles and select the new one
      const updatedRoles = await handleGetRolesByCompanyIdApi(formData.companyId as number);
      setRoleData(updatedRoles);
      const newRole = updatedRoles.find((r) => r.name === newRoleName);
      if (newRole) {
        handleOnChange("roleId", String(newRole.id));
        setSelectedRole(newRole);
      }

      setShowCreateRole(false);
      setNewRoleName("");
      setSelectedPermissions([]);
      setIsCreatingRole(false);
      showSuccess("Role created successfully");
    } catch (err) {
      setIsCreatingRole(false);
      showError("Error creating role");
      console.error(err);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <NativeSelect
                name="companyId"
                value={formData.companyId}
                input={<OutlinedInput sx={selectSx} />}
                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
              >
                <option value="">Select a company *</option>
                {companyData.map((result, index) => (
                  <option value={result.id} key={index}>{result.name}</option>
                ))}
              </NativeSelect>
            </FormControl>
            <TextField
              label="First name *"
              fullWidth
              value={formData.firstName}
              name="firstName"
              onChange={(e) => handleOnChange(e.target.name, e.target.value)}
            />
            <TextField
              label="Last name *"
              fullWidth
              value={formData.lastName}
              name="lastName"
              onChange={(e) => handleOnChange(e.target.name, e.target.value)}
            />
            <TextField
              label="Email *"
              fullWidth
              type="email"
              value={formData.email}
              name="email"
              onChange={(e) => handleOnChange(e.target.name, e.target.value)}
              inputProps={{ maxLength: 245 }}
              error={!!formData.email && !isValidEmail(formData.email)}
              helperText={formData.email && !isValidEmail(formData.email) ? "Enter a valid email address" : ""}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControl sx={{ flex: 1 }}>
                  <NativeSelect
                    name="roleId"
                    value={formData.roleId}
                    input={<OutlinedInput sx={selectSx} />}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      handleOnChange(name, value);
                      setShowCreateRole(false);
                      if (value === "no-export") {
                        setSelectedRole(undefined);
                      } else {
                        setSelectedRole(roleData.find((role) => role.id === parseInt(value)));
                      }
                    }}
                  >
                    <option value={0}>Select a role *</option>
                    <option value="no-export">No Export</option>
                    {roleData.map((result, index) => (
                      <option value={result.id} key={index}>{result.name}</option>
                    ))}
                  </NativeSelect>
                </FormControl>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateRole}
                  sx={{ whiteSpace: "nowrap", color: theme.palette.brand.primary, textTransform: "none", fontWeight: 500 }}
                >
                  New role
                </Button>
              </Box>

              {showCreateRole && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: "8px",
                    border: "1px solid #CCEFEA",
                    backgroundColor: "#F7FDFC",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                    Create a new role
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Role name *"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {globalPermissions.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: "#878787", mb: 1, display: "block" }}>
                        Assign permissions
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {globalPermissions.map((perm) => (
                          <Chip
                            key={perm.id}
                            label={perm.name}
                            size="small"
                            clickable
                            color={selectedPermissions.includes(perm.name) ? "primary" : "default"}
                            variant={selectedPermissions.includes(perm.name) ? "filled" : "outlined"}
                            onClick={() => {
                              setSelectedPermissions((prev) =>
                                prev.includes(perm.name)
                                  ? prev.filter((p) => p !== perm.name)
                                  : [...prev, perm.name]
                              );
                            }}
                            sx={selectedPermissions.includes(perm.name) ? { backgroundColor: theme.palette.brand.primary, color: "#fff" } : {}}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      onClick={() => { setShowCreateRole(false); setNewRoleName(""); setSelectedPermissions([]); }}
                      sx={{ textTransform: "none", color: "#878787" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleCreateNewRole}
                      disabled={!newRoleName.trim() || isCreatingRole}
                      sx={{ textTransform: "none", backgroundColor: theme.palette.brand.success, "&:hover": { backgroundColor: theme.palette.brand.success, opacity: 0.9 } }}
                    >
                      {isCreatingRole ? "Creating..." : "Create role"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {selectedRole?.permissionsObj && selectedRole.permissionsObj.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ color: "#878787", mb: 1 }}>
                  Permissions for selected role
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selectedRole.permissionsObj.map((permission, index) => (
                    <Chip label={permission.permissionName} key={index} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            <FormControl fullWidth>
              <NativeSelect
                name="departmentId"
                value={formData.departmentId}
                input={<OutlinedInput sx={selectSx} />}
                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
              >
                <option value={undefined}>Select a department *</option>
                {departments.map((result, index) => (
                  <option value={result.id} key={index}>{result.name}</option>
                ))}
              </NativeSelect>
            </FormControl>

            <FormControl fullWidth>
              <NativeSelect
                name="websiteRedirect"
                value={formData.websiteRedirect}
                input={<OutlinedInput sx={selectSx} />}
                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
              >
                <option value={undefined}>Select a website redirect *</option>
                {WEBSITE_REDIRECT_OPTIONS.map((option) => (
                  <option value={option.value} key={option.label}>{option.label}</option>
                ))}
              </NativeSelect>
            </FormControl>
          </Box>
        );
    }
  };

  return (
    <ModalWrapper
      open={openModal}
      onClose={handleClose}
      title={formAction === EFormAction.CREATE ? "Create User" : "Update User"}
      actions={
        activeStep === 0 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            disabled={!formData.companyId || !formData.firstName?.trim() || !formData.lastName?.trim() || !isValidEmail(formData.email || "")}
            sx={modalSubmitButtonSx}
          >
            Next
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={() => setActiveStep(0)} sx={modalCancelButtonSx}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={formAction === EFormAction.CREATE ? handleCreateUser : handleUpdateUser}
              sx={modalSubmitButtonSx}
            >
              {formAction === EFormAction.CREATE ? "Create" : "Update"}
            </Button>
          </>
        )
      }
    >
      <Stepper
        activeStep={activeStep}
        sx={{
          mb: 2,
          "& .MuiStepIcon-root.Mui-active": { color: theme.palette.brand.primary },
          "& .MuiStepIcon-root.Mui-completed": { color: theme.palette.brand.success },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {renderStepContent()}
    </ModalWrapper>
  );
};

export default UserModal;
